import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-gray-200';
  
  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  };

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: '',
    rounded: 'rounded-lg',
  };

  const style: React.CSSProperties = {
    width: width,
    height: height || (variant === 'text' ? '1em' : undefined),
  };

  return (
    <div
      className={`${baseClasses} ${animationClasses[animation]} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

// Skeleton for table rows
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 5 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </td>
    ))}
  </tr>
);

// Skeleton for cards
export const CardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-4 animate-pulse ${className}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="h-5 bg-gray-200 rounded w-32"></div>
        <div className="h-5 bg-gray-200 rounded-full w-16"></div>
      </div>
      <div className="h-4 w-4 bg-gray-200 rounded"></div>
    </div>
    <div className="flex items-center gap-2 mb-3">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-4 bg-gray-200 rounded w-20"></div>
    </div>
    <div className="mb-3">
      <div className="h-2 bg-gray-200 rounded-full w-full"></div>
    </div>
    <div className="flex items-center gap-4">
      <div className="h-4 bg-gray-200 rounded w-20"></div>
      <div className="h-4 bg-gray-200 rounded w-24"></div>
    </div>
  </div>
);

// Skeleton for process/task list items
export const ListItemSkeleton: React.FC = () => (
  <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 animate-pulse">
    <div className="col-span-4">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
    <div className="col-span-2">
      <div className="h-6 bg-gray-200 rounded-full w-20"></div>
    </div>
    <div className="col-span-2">
      <div className="h-4 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="col-span-2">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="col-span-2 text-right">
      <div className="h-8 bg-gray-200 rounded w-16 ml-auto"></div>
    </div>
  </div>
);

// Skeleton for stats cards
export const StatsCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="w-5 h-5 bg-gray-200 rounded"></div>
      <div className="h-3 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="h-8 bg-gray-200 rounded w-16 mb-1"></div>
    <div className="h-4 bg-gray-200 rounded w-20"></div>
  </div>
);

// Skeleton for sidebar details
export const DetailsSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="p-4 border-b border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
          <div className="h-5 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="flex gap-1">
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
      <div className="h-6 bg-gray-200 rounded-full w-20"></div>
    </div>
    <div className="p-4">
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="h-3 bg-gray-200 rounded w-16 mb-3"></div>
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center p-2 bg-white rounded border border-gray-100">
              <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto"></div>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-3">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-2 bg-gray-200 rounded-full w-full"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Skeleton for incident cards
export const IncidentCardSkeleton: React.FC = () => (
  <div className="p-4 border-b border-gray-200 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-4 h-4 bg-gray-200 rounded-full mt-1"></div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-64"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-6 bg-gray-200 rounded-full w-16"></div>
            <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2">
          <div className="h-3 bg-gray-200 rounded w-24"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for user/integration cards
export const GridCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="h-5 bg-gray-200 rounded-full w-16"></div>
      <div className="h-5 bg-gray-200 rounded-full w-12"></div>
    </div>
  </div>
);

// Skeleton for analytics table
export const AnalyticsTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="animate-pulse">
    <div className="bg-gray-50 px-6 py-3 flex gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="h-4 bg-gray-200 rounded w-20"></div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="px-6 py-4 border-b border-gray-200 flex gap-4">
        {[1, 2, 3, 4, 5, 6].map((j) => (
          <div key={j} className="h-4 bg-gray-200 rounded w-20"></div>
        ))}
      </div>
    ))}
  </div>
);

// Full page loading skeleton
export const PageSkeleton: React.FC<{}> = () => (
  <div className="animate-pulse">
    <div className="flex items-center gap-4 mb-6">
      <div className="p-2 bg-gray-200 rounded-lg w-10 h-10"></div>
      <div>
        <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-48"></div>
      </div>
    </div>
    <div className="grid grid-cols-4 gap-6 mb-8">
      {[1, 2, 3, 4].map((i) => (
        <StatsCardSkeleton key={i} />
      ))}
    </div>
    <div className="bg-white rounded-lg border border-gray-200">
      {[1, 2, 3, 4, 5].map((i) => (
        <ListItemSkeleton key={i} />
      ))}
    </div>
  </div>
);

export default Skeleton;
